import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comicList } from '@/mocks/comics';
import { categories, categoryComicsMap } from '@/mocks/categories';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-900">分类未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:underline">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const comicIds = categoryComicsMap[category.slug] || [];
  const categoryComics = comicList.filter((c) => comicIds.includes(c.id));

  const collectionJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name}韩漫 - 韩国漫画大全 | 韩漫网`,
    description: `${category.description}，共收录${category.count}部韩国漫画作品，涵盖最新热门与经典佳作。`,
    url: `http://h8wx1.com/category/${category.slug}`,
    hasPart: categoryComics.map((comic) => ({
      '@type': 'CreativeWork',
      name: comic.title,
      url: `http://h8wx1.com/article/${comic.id}`,
      author: {
        '@type': 'Person',
        name: comic.author,
      },
    })),
  };

  const categoryTitle = `${category.name}韩漫_免费${category.name}韩国漫画大全_下拉式在线阅读-韩漫网`;
  const categoryDesc = `${category.description}，共收录${category.count}部${category.name}类韩国漫画，涵盖最新热门${category.name}韩漫与经典佳作，免费下拉式在线阅读，韩漫网精选推荐。`;
  const categoryKeywords = `${category.name}韩漫,韩国漫画,${category.name}漫画大全,韩漫网,免费漫画`;

  return (
    <>
      <title>{categoryTitle}</title>
      <meta name="description" content={categoryDesc} />
      <meta name="keywords" content={categoryKeywords} />
      <link rel="canonical" href={`http://h8wx1.com/category/${category.slug}`} />
      <meta name="last-modified" content="2026-07-03" />
      <meta property="og:title" content={categoryTitle} />
      <meta property="og:description" content={categoryDesc} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`http://h8wx1.com/category/${category.slug}`} />
      <meta property="og:locale" content="zh_CN" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={categoryTitle} />
      <meta name="twitter:description" content={categoryDesc} />
      <Navbar />
      <main>
        <script type="application/ld+json">{JSON.stringify(collectionJsonLd)}</script>

        {/* Hero */}
        <section className="relative w-full min-h-[420px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-foreground-950"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/90 via-foreground-950/70 to-foreground-950/95"></div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center pt-20 pb-16">
            <p className="text-xs text-primary-300 tracking-widest mb-4 uppercase">
              CATEGORY
            </p>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-background-50 leading-tight">
              {category.name}韩漫
              <br />
              精选推荐
            </h1>
            <p className="mt-6 text-sm md:text-base text-background-50/70 max-w-2xl mx-auto leading-relaxed">
              {category.description}，共收录{category.count}部作品，涵盖最新热门与经典佳作。
            </p>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background-50 to-transparent"></div>
        </section>

        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { name: '首页', path: '/' },
            { name: category.name },
          ]}
        />

        {/* Sub-category intro */}
        <section className="w-full py-6 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-600 whitespace-nowrap">热门标签：</span>
              {categoryComics.flatMap((c) => c.tags).filter((v, i, a) => a.indexOf(v) === i).map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap cursor-pointer hover:bg-secondary-200 transition-colors duration-200"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Comic Grid */}
        <section className="w-full py-10 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
                {category.name}韩漫大全
              </h2>
              <span className="text-sm text-foreground-400 whitespace-nowrap">
                共{categoryComics.length}部
              </span>
            </div>

            {categoryComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                {categoryComics.map((comic) => (
                  <ComicCard key={comic.id} {...comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-foreground-500">该分类暂无漫画，敬请期待</p>
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="w-full py-14 bg-primary-500">
          <div className="w-full px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl text-background-50">
              发现更多精彩韩漫
            </h2>
            <p className="mt-3 text-sm text-background-50/80">
              浏览其他分类，找到你的下一部心头好
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              {categories.filter((c) => c.slug !== slug).slice(0, 4).map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.slug}`}
                  className="px-5 py-2.5 bg-background-50/20 text-background-50 text-sm rounded-full whitespace-nowrap hover:bg-background-50/30 transition-all duration-300 cursor-pointer"
                >
                  {c.name}韩漫
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}